import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import { RootState } from '../store';
import { Text } from './Text';
import CountdownTimer from './CountdownTimer';
import Carrousel from './atoms/Carrousel/Carrousel';
import AdminSectionTitle from './AdminSectionTitle';
import CouponHeadsUp from './CouponHeadsUp';
import useEventPixel from '../hooks/useEventPixel';
import { parseFriendlyDate } from '../utils/dates';
import BackgroundBannerImage from './BackgroundBannerImage';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';

const CreateOrderOutlet: React.FC = () => {
  const event = useSelector((state: RootState) => state.events.selectedEvent);
  const location = useLocation();
  const navigate = useNavigateWithQuery();
  const isBackoffice = location.pathname.includes('admin');
  const showPoll = event?.has_polls && !isBackoffice;
  const isSeatsPage = location.pathname.includes('orders/new/seats');
  const isTimeoutPage = location.pathname.includes('orders/new/timeout');
  const reservation = useSelector((state: RootState) => state.orders.reservation);

  const currentStep = () => {
    const url = location.pathname;
    if (url.includes('orders/new/tickets') || url.includes('orders/new/manual') || isSeatsPage) {
      return 1;
    }
    if (url.includes('orders/new/entry-data')) {
      return 2;
    }
    if (url.includes('orders/new/poll')) {
      return 3;
    }
    if (url.includes('orders/new/checkout')) {
      return showPoll ? 4 : 3;
    }
    return 0;
  };

  const getStepsNumber = () => (showPoll ? 4 : 3);

  // Initialize Facebook Pixel with event's custom pixel ID (only for non-backoffice)
  useEventPixel(event, { trackPageView: true, enabled: !isBackoffice });
  if (!event) {
    navigate('../../');
  }

  return (
    <>
      {!isBackoffice && event && <BackgroundBannerImage event={event} />}
      <div className='pb-3 my-6 lg:my-12 min-h-[calc(100vh-300px)]'>
        <CouponHeadsUp />
        {isBackoffice && (
          <div className='mb-8'>
            <AdminSectionTitle
              title="Eventos"
              subtitle={`${event?.name} - Vender manualmente`}
            />
          </div>
        )}
        <div className='grid grid-cols-1 lg:grid-cols-11 lg:gap-12 lg:px-8'>
          <div className="mb-8 lg:col-span-4 flex flex-col gap-4">
            <Carrousel images={event?.banner_image ? [{ url: event.banner_image, eventId: event.id }] : []} />
            <div className='glass-surface text-center lg:text-left'>
              <h1 className='text-2xl lg:text-3xl font-medium'>{event?.name}</h1>
              <span className='text-lg lg:text-xl text-text-primary'>{parseFriendlyDate(event?.date || '', true, true)}</span>
            </div>
          </div>
          <div className='mb-4 lg:col-span-7 glass-surface'>
            {currentStep() !== 0 && <div className="flex flex-col p-[8px] pl-[16px] align-center justify-center items-center glass-surface shadow rounded-3xl mb-4">
              {!isTimeoutPage && <div className="font-md-normal text-text-primary leading-7">Paso {currentStep()} de {getStepsNumber()}</div>}
              {isSeatsPage && <Text className='mt-3' size='md' color='text-text-primary'>Seleccione asientos en el mapa de la sala</Text>}
            </div>}
            {reservation && !isTimeoutPage && <div className='mt-[-10px] mb-1'><CountdownTimer expirationDate={new Date(reservation.expires_at)} /></div>}
            <Outlet key={location.pathname} />
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateOrderOutlet;
